import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const Speaking = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'speaking'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container speaking-page">
      <h1 className="page-title">Effective Speaking</h1>

      {/* Include the image */}
      <img
        src="/images/wf-speaking.webp"
        alt="Effective Speaking"
        className="page-image"
      />

      <p className="page-text">Welcome to the Effective Speaking section of Wholeness Forward.</p>

      <p className="page-text">
        Effective speaking is a vital skill that can influence how we connect with others, share ideas, and inspire change.
        Whether you're giving a presentation, leading a meeting, or engaging in a casual conversation, how you speak matters.
        Speaking with clarity, confidence, and empathy can foster understanding, build relationships, and achieve desired outcomes.
      </p>
      <p className="page-text">
        In this section, we will explore how to communicate effectively, how to express yourself clearly, and how to influence and persuade others
        with the power of your words.
      </p>

      <hr className="page-divider" />

      <h2 className="page-heading">Subtopics</h2>
      <ul className="content-list">
        <li><strong>Clarity:</strong> Ensuring your message is clear and easy to understand.</li>
        <li><strong>Tone & Pace:</strong> Using your voice to convey the right emotion and keep your audience engaged.</li>
        <li><strong>Persuasion:</strong> How to use your words to influence others and convey your message powerfully.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="page-heading">Curated Video Collection</h2>
      {loading ? (
        <p>Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  width="560"
                  height="315"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
                <hr />
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}

      <hr className="page-divider" />

      <h3 className="subheading">Join the Speaking Skills Community</h3>
      <p className="page-text">
        Share your experiences, ask questions, and gain insights from others working on their speaking skills.
        Whether you’re preparing for a big presentation or simply want to improve your everyday communication,
        this is the place to connect and learn.
      </p>
      <a href="/forum/Speaking" className="btn">Join the Forum</a>
    </div>
  );
};

export default Speaking;
